import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import {
    getOptionsFromDefinition,
    getOptionText,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";
import { UserSubCellEditor } from "commonComponents/entity/customField/utils/customFieldTypes/UserSubCellEditor";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

import {
    type CustomFieldAssociatedType,
    type ListOfSubsCustomFieldDefinition,
    type ListOfSubsCustomFieldValue,
} from "handlers";

export const ListOfSubsCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    ListOfSubsCustomFieldValue,
    ListOfSubsCustomFieldDefinition
> = {
    getValue(field): string | null {
        return field.selectedSubId != null && field.selectedSubId > 0
            ? String(field.selectedSubId)
            : "";
    },

    setValue(field, value): ListOfSubsCustomFieldValue {
        const parsed = value ? parseInt(value, 10) : -1;
        return {
            ...field,
            selectedSubId: isNaN(parsed) ? -1 : parsed,
        };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): ListOfSubsCustomFieldValue {
        return {
            dataType: "ListOfSubs",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            selectedSubId: value ? parseInt(value, 10) : -1,
        };
    },

    compare(valueA: string | null, valueB: string | null, definition?): number {
        const textA = valueA ? getOptionText(parseInt(valueA, 10), definition) : "";
        const textB = valueB ? getOptionText(parseInt(valueB, 10), definition) : "";
        return textA.localeCompare(textB, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null, definition): ReactNode {
        if (!value) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const subId = parseInt(value, 10);
        if (isNaN(subId)) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const displayText = getOptionText(subId, definition);
        return <BTText size="normal-md" text={displayText} />;
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        _autoFocus?: boolean,
        definition?: ListOfSubsCustomFieldDefinition,
        _field?: ListOfSubsCustomFieldValue,
        formMode?: "always-editing" | "click-to-edit",
    ): ReactNode {
        const treeData = getOptionsFromDefinition(definition);
        const parsedValue = value ? parseInt(value, 10) : null;
        const selectedValues =
            parsedValue != null && !isNaN(parsedValue) ? [parsedValue] : undefined;

        return (
            <BTSelect
                id={`listOfSubsCustomFieldEditor-${customFieldId}`}
                data-testid="listOfSubsCustomFieldEditor"
                value={selectedValues}
                treeData={treeData}
                onChange={(_id, val) => {
                    const selected = val as number;
                    onChange(selected != null ? String(selected) : null);
                }}
                onBlur={() => onBlur()}
                multiple={false}
                formMode={formMode ?? "click-to-edit"}
                placeholder={friendlyDefaultDisplayValue}
            />
        );
    },

    getAgGridCellEditor() {
        return UserSubCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
